import { ApiRequestError, isUnauthorizedError } from "@/api/util";

type RunWithRetryOptions = {
  /** Retries on top of the first attempt. */
  attempts: number;
  signal?: AbortSignal;
};

/** Only transient failures are worth a second attempt.
 *
 * A missing status means the request never got an answer (network, DNS,
 * timeout). 5xx is the server failing to do something it should be able to
 * do. Everything else — 400, 409, 413 — is deterministic, and a 401 has
 * already triggered the logout redirect in the axios interceptor, so retrying
 * it just races that navigation. */
export const isRetryableUploadError = (error: unknown): boolean => {
  if (isUnauthorizedError(error)) {
    return false;
  }

  if (!(error instanceof ApiRequestError)) {
    return false;
  }

  return error.status === undefined || error.status >= 500;
};

export const runWithRetry = async <TResult>(
  fn: () => Promise<TResult>,
  { attempts, signal }: RunWithRetryOptions,
): Promise<TResult> => {
  let attempt = 0;

  for (;;) {
    try {
      return await fn();
    } catch (error) {
      // Must come before the predicate: normalizeAxiosError turns axios's
      // CanceledError into an ApiRequestError with no status, which is
      // indistinguishable from a genuine network failure.
      if (signal?.aborted || attempt >= attempts) {
        throw error;
      }

      if (!isRetryableUploadError(error)) {
        throw error;
      }

      attempt += 1;
    }
  }
};
